// Design tokens for the v2 UI, ported from frontend/DESIGN-notion.md.
//
// The light palette is the Notion system verbatim: a warm paper canvas,
// near-black ink, hairline borders, and exactly one structural accent (blue).
// The dark palette is a polarity flip derived from the same roles — dark
// charcoal canvas, light ink, the same single blue (brightened one step for
// contrast on dark surfaces).

export type PaletteMode = 'light' | 'dark';

// ---------------------------------------------------------------------------
// Color roles
// ---------------------------------------------------------------------------

export interface ColorTokens {
  /** Single structural accent — CTAs, links, active nav indicator. */
  primary: string;
  primaryActive: string;
  onPrimary: string;
  /** Deep indigo — reserved for one "hero" moment (login / empty states). */
  secondary: string;
  /** Page canvas (warm paper in light mode). */
  canvas: string;
  /** Soft canvas band — sidebar, table headers, footers. */
  canvasSoft: string;
  /** Card / panel / input surface. */
  surface: string;
  /** Primary text. */
  ink: string;
  /** Secondary body copy. */
  inkSecondary: string;
  /** Supporting / muted copy. */
  inkMuted: string;
  /** Captions, metadata, placeholders. */
  inkFaint: string;
  /** 1px borders and dividers. */
  hairline: string;
  /** Hover wash for list rows / nav rows. */
  hoverWash: string;
}

export const lightColors: ColorTokens = {
  primary: '#0075de',
  primaryActive: '#005bab',
  onPrimary: '#ffffff',
  secondary: '#213183',
  canvas: '#f6f5f4',
  canvasSoft: '#f1f0ee',
  surface: '#ffffff',
  ink: 'rgba(0, 0, 0, 0.95)',
  inkSecondary: '#31302e',
  inkMuted: '#615d59',
  inkFaint: '#a39e98',
  hairline: '#e6e6e6',
  hoverWash: 'rgba(0, 0, 0, 0.04)',
};

export const darkColors: ColorTokens = {
  primary: '#2f8fe4',
  primaryActive: '#5aa7ec',
  onPrimary: '#ffffff',
  secondary: '#8d9ce0',
  canvas: '#191919',
  canvasSoft: '#202020',
  surface: '#252525',
  ink: 'rgba(255, 255, 255, 0.92)',
  inkSecondary: '#d3d1cb',
  inkMuted: '#9b9a97',
  inkFaint: '#6f6e69',
  hairline: 'rgba(255, 255, 255, 0.11)',
  hoverWash: 'rgba(255, 255, 255, 0.055)',
};

export const getColors = (mode: PaletteMode): ColorTokens =>
  mode === 'dark' ? darkColors : lightColors;

// ---------------------------------------------------------------------------
// Decorative "sticker" palette — data categories only, never chrome or CTAs.
// ---------------------------------------------------------------------------

export const sticker = {
  sky: '#62aef0',
  purple: '#d6b6f6',
  purpleDeep: '#391c57',
  pink: '#ff64c8',
  orange: '#dd5b00',
  orangeDeep: '#793400',
  teal: '#2a9d99',
  green: '#1aae39',
  brown: '#523410',
} as const;

// ---------------------------------------------------------------------------
// Typography — Inter everywhere, tight negative tracking at display sizes.
// ---------------------------------------------------------------------------

export const fontFamily = [
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Helvetica',
  'Arial',
  'sans-serif',
].join(',');

export const type = {
  heading1: { fontSize: 40, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-1px' },
  heading2: { fontSize: 26, fontWeight: 700, lineHeight: 1.23, letterSpacing: '-0.625px' },
  heading3: { fontSize: 22, fontWeight: 700, lineHeight: 1.27, letterSpacing: '-0.25px' },
  title: { fontSize: 20, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.125px' },
  bodyMd: { fontSize: 16, fontWeight: 400, lineHeight: 1.5, letterSpacing: 0 },
  bodySm: { fontSize: 15, fontWeight: 400, lineHeight: 1.33, letterSpacing: 0 },
  button: { fontSize: 15, fontWeight: 500, lineHeight: 1.5, letterSpacing: 0 },
  caption: { fontSize: 14, fontWeight: 400, lineHeight: 1.43, letterSpacing: 0 },
  eyebrow: { fontSize: 12, fontWeight: 600, lineHeight: 1.33, letterSpacing: '0.125px' },
} as const;

// ---------------------------------------------------------------------------
// Shape & spacing
// ---------------------------------------------------------------------------

export const rounded = {
  xs: 4,
  sm: 5,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

// MUI's default 8px spacing unit matches the Notion base unit; tokens here are
// for non-MUI contexts (raw CSS values).
export const spacing = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 28,
  xxl: 32,
} as const;

// ---------------------------------------------------------------------------
// Elevation — barely-there layered shadows; most surfaces use hairline only.
// ---------------------------------------------------------------------------

export const elevation = {
  /** Level 0 — flat: hairline border, no shadow. */
  flat: 'none',
  /** Level 1 — soft: raised cards, floating buttons. */
  soft:
    'rgba(0, 0, 0, 0.01) 0px 0.175px 1.041px, rgba(0, 0, 0, 0.02) 0px 0.8px 2.925px, ' +
    'rgba(0, 0, 0, 0.027) 0px 2.025px 7.847px, rgba(0, 0, 0, 0.04) 0px 4px 18px',
  /** Level 2 — elevated: modals, popovers, menus. */
  elevated:
    'rgba(0, 0, 0, 0.02) 0px 1px 2px, rgba(0, 0, 0, 0.03) 0px 3px 6px, ' +
    'rgba(0, 0, 0, 0.04) 0px 7px 14px, rgba(0, 0, 0, 0.05) 0px 14px 28px, ' +
    'rgba(0, 0, 0, 0.05) 0px 23px 52px',
} as const;

export const darkElevation = {
  flat: 'none',
  soft:
    'rgba(0, 0, 0, 0.1) 0px 0.175px 1.041px, rgba(0, 0, 0, 0.14) 0px 0.8px 2.925px, ' +
    'rgba(0, 0, 0, 0.16) 0px 2.025px 7.847px, rgba(0, 0, 0, 0.2) 0px 4px 18px',
  elevated:
    'rgba(0, 0, 0, 0.18) 0px 1px 2px, rgba(0, 0, 0, 0.2) 0px 3px 6px, ' +
    'rgba(0, 0, 0, 0.22) 0px 7px 14px, rgba(0, 0, 0, 0.25) 0px 14px 28px, ' +
    'rgba(0, 0, 0, 0.3) 0px 23px 52px',
} as const;

export const getElevation = (mode: PaletteMode) =>
  mode === 'dark' ? darkElevation : elevation;
